import debounce from 'lodash/debounce';
import { Logger } from '../support/Logger';
import { DebouncedFunc } from 'lodash';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { ITrackingContext } from './MixpanelService';

// re-exporting for conveniece to consumers
export {  type ITrackingContext };

/**
 * A strongly typed wrapper around BufferedEventBus specifically for MixPanel.
 *
 * Primary concerns of this calss
 * 1. encapsulate the name of the event on the bridge, and the schema of the payload
 *    to reduce related errors / improve DX
 * 2. minimal dependencies so this can be instantiated immediately in the app container
 */
export class MixPanelEventEmitter {
    static inject = () => [BufferedEventBus, Logger];
    constructor(private events: BufferedEventBus, private log: Logger){}

    onTrack = (listener: (event: string, context: ITrackingContext) => void) => {
        return this.events.on('MixPanelService.track', (payload) => {
            const { event, context } = payload as { event: string; context: ITrackingContext };
            listener(event, context);
        });
    };

    track = (event: string, getContext?: () => ITrackingContext) => {
        try {
            const context = getContext ? getContext() : undefined;
            this.sendToBridge(event, context);
        } catch (e: any) {
            this.log.error(new Error(`Failed to track event "${event}": ${e.message}`));
        }
    };

    private eventsToDebounceFunction: { [event:string]: DebouncedFunc<(event: string, getContext?: () => ITrackingContext) => void>} = {};

    trackWithDebounce = ({ event, getContext, waitMS = 2000 }: {event: string, getContext?: () => ITrackingContext, waitMS?: number }) => {
        try {
            if (!this.eventsToDebounceFunction[event]){
                this.eventsToDebounceFunction[event] = debounce(this.track, waitMS);
            }

            this.eventsToDebounceFunction[event](event, getContext);

        } catch (e: any) {
            this.log.error(new Error(`Failed to track event with debounce "${event}": ${e.message}`));
        }
    };

    private sendToBridge = (event: string, context?: ITrackingContext) => {
        context = { ...context };
        this.log.info(`Track "${event}"`, context);

        this.events.emit('MixPanelService.track', { event, context });
    };
}
